import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { Bell, LogOut, Menu, X, Heart } from "lucide-react";
import { useState } from "react";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="brand-logo" aria-label="Hello Foundation home">
    <img
      src="/hello-foundation-logo.png"
      alt="Hello Foundation"
      width={800}
      height={436}
      loading="eager"
      decoding="async"
      className="h-11 sm:h-12 md:h-14 w-auto object-contain"
      draggable="false"
    />
  </Link>
);

const linkClass = (active) =>
  `px-4 py-2 rounded-full text-sm font-semibold transition-all ${
    active ? "bg-primary text-white shadow-md" : "text-[#3D405B] hover:bg-[#3D405B]/5"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const { totalUnread = 0, pendingSessionReady = [], pendingClientJoined = [] } = useChat() || {};
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // "Join now" bell (client-facing) — counsellor pressed Start
  const nextReady = pendingSessionReady[0];
  // "Client here" bell (counsellor-facing) — client tapped Join
  const nextClientJoined = pendingClientJoined[0];

  const links = user
    ? user.role === "admin"
      ? [
          { to: "/dashboard", label: "Dashboard" },
          { to: "/admin", label: "Admin" },
          { to: "/case-files", label: "Case files" },
          { to: "/self-help", label: "Self-Help" },
          { to: "/chat", label: "Chat" },
        ]
      : user.role === "counsellor"
        ? [
            { to: "/dashboard", label: "Dashboard" },
            { to: "/availability", label: "Availability" },
            { to: "/case-files", label: "Case files" },
            { to: "/chat", label: "Chat" },
            { to: "/self-help", label: "Self-Help" },
          ]
        : [
            { to: "/dashboard", label: "My Sessions" },
            { to: "/book", label: "Book" },
            { to: "/chat", label: "Chat" },
            { to: "/self-help", label: "Self-Help" },
          ]
    : [];

  return (
    <header className="sticky top-0 z-40 bg-[#F9F6F0] border-b border-[#3D405B]/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className={linkClass(pathname.startsWith(l.to))} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="inline-flex items-center gap-1.5">
                {l.label}
                {l.to === "/chat" && totalUnread > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary text-white text-[10px] font-extrabold leading-none" data-testid="nav-chat-unread">
                    {totalUnread > 9 ? "9+" : totalUnread}
                  </span>
                )}
              </span>
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {nextReady && (
                <Link
                  to={`/session/${nextReady.booking_id}`}
                  className="relative inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary/15 text-secondary hover:bg-secondary/25 transition-all font-bold text-xs uppercase tracking-wider"
                  data-testid="nav-session-ready-badge"
                  title={`${nextReady.counsellor_name || "Your counsellor"} is ready — join now`}
                >
                  <span className="relative inline-flex">
                    <Bell size={15} />
                    <span aria-hidden className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary animate-pulse ring-2 ring-[#F9F6F0]" />
                  </span>
                  Join now
                  {pendingSessionReady.length > 1 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[9px] font-extrabold leading-none">
                      {pendingSessionReady.length}
                    </span>
                  )}
                </Link>
              )}
              {nextClientJoined && (
                <Link
                  to={`/session/${nextClientJoined.booking_id}`}
                  className="relative inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-all font-bold text-xs uppercase tracking-wider"
                  data-testid="nav-client-joined-badge"
                  title={`${nextClientJoined.client_name || "Your client"} has joined the session`}
                >
                  <span className="relative inline-flex">
                    <Bell size={15} />
                    <span aria-hidden className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-500 animate-pulse ring-2 ring-[#F9F6F0]" />
                  </span>
                  Client here
                  {pendingClientJoined.length > 1 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-secondary text-white text-[9px] font-extrabold leading-none">
                      {pendingClientJoined.length}
                    </span>
                  )}
                </Link>
              )}
              <Link to="/profile" className="flex items-center gap-2 pr-1 hover:opacity-80 transition-opacity" data-testid="navbar-profile-link" aria-label="Edit profile">
                <div className="text-right">
                  <div className="text-sm font-bold text-[#3D405B]" data-testid="navbar-user-name">{user.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A7D96] font-bold">{user.role}</div>
                </div>
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" data-testid="navbar-user-picture" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-sm font-extrabold text-[#3D405B]">{user.name?.[0]?.toUpperCase() || "?"}</div>
                )}
              </Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="rounded-full p-2.5 hover:bg-[#3D405B]/5 text-[#3D405B] transition-colors" data-testid="navbar-logout-btn" aria-label="Sign out">
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/counsellor-apply" className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-secondary border-2 border-secondary/40 hover:bg-secondary/10 transition-colors" data-testid="navbar-counsellor-apply-link">
                <Heart size={14} strokeWidth={2.5} />
                Volunteer as a counsellor
              </Link>
              <Link to="/login" className="px-5 py-2 rounded-full text-sm font-semibold text-[#3D405B] hover:bg-[#3D405B]/5 transition-colors" data-testid="navbar-login-link">Sign in</Link>
              <Link to="/register" className="px-5 py-2.5 rounded-full text-sm font-bold bg-primary text-white shadow-md hover:shadow-lg transition-shadow" data-testid="navbar-register-link">Get started</Link>
            </>
          )}
        </div>
        <button className="md:hidden p-2 rounded-full hover:bg-[#3D405B]/5" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="navbar-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#3D405B]/5 bg-[#F9F6F0] px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={linkClass(pathname.startsWith(l.to)) + " block w-fit"}>
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/profile" onClick={() => setOpen(false)} className="block text-sm font-semibold text-[#3D405B] pt-2" data-testid="navbar-profile-mobile">Profile</Link>
              <button onClick={async () => { setOpen(false); await logout(); nav("/"); }} className="block text-sm font-semibold text-[#3D405B] pt-2" data-testid="navbar-logout-mobile">Sign out</button>
            </>
          ) : (
            <div className="pt-2 flex flex-col gap-2">
              <Link to="/counsellor-apply" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold text-secondary border-2 border-secondary/40 w-fit" data-testid="navbar-counsellor-apply-mobile">
                <Heart size={14} strokeWidth={2.5} />
                Volunteer as a counsellor
              </Link>
              <div className="flex gap-2">
                <Link to="/login" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-[#3D405B] border border-[#3D405B]/15">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-bold bg-primary text-white">Get started</Link>
              </div>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
